import { getPartStyleFromStyleInterpolation } from '@frontend/components/src/theme/styleConfigHelpers';

import type { CheckboxPart } from '../checkbox';
import { CHECKBOX_STYLE_CONFIG } from '../checkbox/checkboxStyleConfig';
import { DEFAULT_PROPS } from './const';
import { RADIO_PARTS } from './radioParts';
import type { RadioStyleConfig } from './types';

const baseStyle: RadioStyleConfig['baseStyle'] = (styleFunctionProps) => {
  const getCheckboxStylesForPart = (part: CheckboxPart) =>
    getPartStyleFromStyleInterpolation(
      CHECKBOX_STYLE_CONFIG.baseStyle,
      part,
      styleFunctionProps,
    );

  const checkboxContainerStyles = getCheckboxStylesForPart('container');
  const checkboxLabelStyles = getCheckboxStylesForPart('label');
  const checkboxControlStyles = getCheckboxStylesForPart('control');

  return {
    container: {
      ...checkboxContainerStyles,
      cursor: 'pointer',
      _disabled: {
        cursor: 'not-allowed',
      },
    },
    label: checkboxLabelStyles,
    control: {
      ...checkboxControlStyles,
      borderRadius: 'full',
      _checked: {
        ...checkboxControlStyles?.['_checked'],
        bg: 'page.surface',
        color: 'interaction.main',
        _hover: {
          borderColor: 'interaction.hover',
          color: 'interaction.hover',
        },
        position: 'relative',
        _before: {
          content: `""`,
          pos: 'absolute',
          borderRadius: '50%',
          bg: 'currentColor',
        },
        _disabled: {
          cursor: 'not-allowed',
          bg: 'page.surfaceDisabled',
          borderColor: 'separator.border',
        },
        _readOnly: {
          bg: 'page.surface',
        },
      },
      _disabled: {
        ...checkboxControlStyles?._disabled,
        cursor: 'not-allowed',
        _before: {
          bg: 'interaction.disabled',
        },
      },
    },
  };
};

const SIZES = {
  sm: {
    control: {
      my: '0.25rem',
      width: 1.5,
      height: 1.5,
      _before: {
        top: 0.25,
        left: 0.25,
        w: 0.75,
        h: 0.75,
      },
    },
    label: { fontSize: 'sm', lineHeight: 'sm' },
  },
  md: {
    control: {
      my: '0.25rem',
      w: 2,
      h: 2,
      _before: {
        top: '0.1875rem',
        left: '0.1875rem',
        w: 1,
        h: 1,
      },
    },
    label: {
      fontSize: 'md',
      lineHeight: 'md',
    },
  },
  lg: {
    control: {
      my: '0.125rem',
      w: 2.5,
      h: 2.5,
      _before: {
        top: 0.5,
        left: 0.5,
        w: '0.625rem',
        h: '0.625rem',
      },
    },
    label: { fontSize: 'lg', lineHeight: 'lg' },
  },
} as const;

export const RADIO_STYLE_CONFIG: RadioStyleConfig = {
  parts: RADIO_PARTS,
  baseStyle,
  sizes: SIZES,
  defaultProps: DEFAULT_PROPS,
};
